import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import toastError from '../../errors/toastError';
import useRoles from '../../hooks/useRoles';

const useStyles = makeStyles(theme => ({
  dialogContent: {
    minWidth: 400,
    maxHeight: 400,
  },
  rolesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  roleChip: {
    margin: theme.spacing(0.5),
  },
  formControl: {
    marginTop: theme.spacing(2),
    minWidth: 200,
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 'bold',
  },
}));

const UserRolesModal = ({ open, onClose, user }) => {
  const classes = useStyles();
  const { addRole, removeRole, getUserRoles, getAvailableRoles } = useRoles();
  
  const [userRoles, setUserRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      loadData();
    }
  }, [open, user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rolesData, availableData] = await Promise.all([
        getUserRoles(user.id),
        getAvailableRoles()
      ]);
      
      setUserRoles(rolesData.map(r => r.role));
      // Filtrar roles que podem ser gerenciadas (excluindo admin e super que são gerenciados separadamente)
      const manageableRoles = availableData.filter(role => 
        !['admin', 'super'].includes(role)
      );
      setAvailableRoles(manageableRoles);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRole = async () => {
    if (!selectedRole) return;
    
    try {
      await addRole(user.id, selectedRole);
      setUserRoles(prev => [...prev, selectedRole]);
      setSelectedRole('');
      toast.success(`Role "${selectedRole}" adicionada com sucesso!`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRemoveRole = async (role) => {
    try {
      await removeRole(user.id, role);
      setUserRoles(prev => prev.filter(r => r !== role));
      toast.success(`Role "${role}" removida com sucesso!`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleClose = () => {
    setSelectedRole('');
    onClose();
  };

  const getRoleColor = (role) => {
    const colors = {
      'gestor': 'primary',
      'usuarios': 'secondary',
      'conexões': 'default',
      'departamentos': 'default',
      'relatorios': 'default',
    };
    return colors[role] || 'default';
  };

  const getRoleLabel = (role) => {
    const labels = {
      'gestor': 'Gestor',
      'usuarios': 'Usuários',
      'conexões': 'Conexões',
      'departamentos': 'Departamentos',
      'relatorios': 'Relatórios',
    };
    return labels[role] || role;
  };

  const availableRolesToAdd = availableRoles.filter(role => !userRoles.includes(role));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Gerenciar Roles - {user?.name}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="h6" className={classes.sectionTitle}>
              Roles Atuais
            </Typography>
            <Box className={classes.rolesContainer}>
              {userRoles.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  Nenhuma role adicional atribuída
                </Typography>
              ) : (
                userRoles.map(role => (
                  <Chip
                    key={role}
                    label={getRoleLabel(role)}
                    color={getRoleColor(role)}
                    className={classes.roleChip}
                    onDelete={() => handleRemoveRole(role)}
                    variant="outlined"
                  />
                ))
              )}
            </Box>

            <Divider />

            <Typography variant="h6" className={classes.sectionTitle}>
              Adicionar Nova Role
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <FormControl className={classes.formControl} variant="outlined" size="small">
                <InputLabel>Selecionar Role</InputLabel>
                <Select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  label="Selecionar Role"
                >
                  {availableRolesToAdd.map(role => (
                    <MenuItem key={role} value={role}>
                      {getRoleLabel(role)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddRole}
                disabled={!selectedRole}
                size="small"
              >
                Adicionar
              </Button>
            </Box>

            <Typography variant="caption" color="textSecondary" style={{ marginTop: 16, display: 'block' }}>
              * As roles "Admin" e "Super" são gerenciadas através do perfil do usuário
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserRolesModal;
